/** Public AdSense settings from GET /api/config (browser-safe). */
export interface AdsPublicConfig {
  ads_enabled: boolean;
  /** Publisher id (ca-pub-…); exposed for site verification even when ads_enabled is false. */
  adsense_client: string | null;
  adsense_slot_quest: string | null;
  adsense_slot_home: string | null;
}

export type AdPlacement = "quest" | "home";

export function parseAdsEnabled(raw: string | undefined): boolean {
  if (raw == null || raw.trim() === "") return false;
  const v = raw.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

export function normalizeAdsenseClient(raw: string | undefined): string | null {
  const v = raw?.trim();
  if (!v) return null;
  return v;
}

/** AdSense loader for site verification (initial HTML head); client must be normalized ca-pub-…. */
export function adsenseLoaderScriptHtml(client: string): string {
  const id = client.trim();
  const src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(id)}`;
  return `<script async src="${src}" crossorigin="anonymous"></script>`;
}

export function htmlIncludesAdsenseLoader(html: string): boolean {
  return /pagead\/js\/adsbygoogle\.js/i.test(html);
}

export function normalizeAdSlot(raw: string | undefined): string | null {
  const v = raw?.trim();
  if (!v) return null;
  return v;
}

/** True when the app may load AdSense and render ad slots. */
export function adsServingReady(config: AdsPublicConfig): boolean {
  if (!config.ads_enabled) return false;
  if (!config.adsense_client) return false;
  if (!config.adsense_slot_quest || !config.adsense_slot_home) return false;
  return true;
}

export function slotForPlacement(config: AdsPublicConfig, placement: AdPlacement): string | null {
  return placement === "quest" ? config.adsense_slot_quest : config.adsense_slot_home;
}

/** ads.txt publisher id from a ca-pub-… client id. */
export function adsTxtPublisherId(client: string): string | null {
  const trimmed = client.trim();
  const m = /^ca-pub-(\d+)$/i.exec(trimmed);
  if (!m) return null;
  return `pub-${m[1]}`;
}

export function buildAdsTxt(client: string): string | null {
  const pub = adsTxtPublisherId(client);
  if (!pub) return null;
  return `google.com, ${pub}, DIRECT, f08c47fec0942fa0\n`;
}

/** Google ad domains to allow in CSP when AdSense is enabled. */
export const ADSENSE_CSP = {
  script: [
    "https://pagead2.googlesyndication.com",
    "https://www.googletagservices.com",
    "https://www.google.com",
    "https://www.gstatic.com",
    "https://fundingchoicesmessages.google.com",
  ],
  frame: [
    "https://googleads.g.doubleclick.net",
    "https://tpc.googlesyndication.com",
    "https://www.google.com",
    "https://fundingchoicesmessages.google.com",
  ],
  img: [
    "https://pagead2.googlesyndication.com",
    "https://www.google.com",
    "https://www.gstatic.com",
    "https://googleads.g.doubleclick.net",
  ],
  connect: [
    "https://pagead2.googlesyndication.com",
    "https://googleads.g.doubleclick.net",
    "https://www.google-analytics.com",
    "https://fundingchoicesmessages.google.com",
  ],
} as const;

/** Merge AdSense hosts into an existing Content-Security-Policy value, or return null if ads are off. */
export function mergeAdsIntoCsp(existing: string | null | undefined, adsOn: boolean): string | null {
  if (!adsOn) return existing?.trim() || null;
  let out = existing?.trim() ?? "";
  const add = (directive: string, hosts: readonly string[]) => {
    const tokens = hosts.join(" ");
    const re = new RegExp(`(^|;\\s*)${directive}\\s+([^;]*)`, "i");
    if (re.test(out)) {
      out = out.replace(re, (_, lead: string, val: string) => {
        const parts = val.trim().split(/\s+/).filter(Boolean);
        for (const h of hosts) if (!parts.includes(h)) parts.push(h);
        return `${lead}${directive} ${parts.join(" ")}`;
      });
      return;
    }
    const sep = out && !out.endsWith(";") ? "; " : out ? " " : "";
    out = `${out}${sep}${directive} ${tokens}`;
  };
  add("script-src", ADSENSE_CSP.script);
  add("frame-src", ADSENSE_CSP.frame);
  add("img-src", ADSENSE_CSP.img);
  add("connect-src", ADSENSE_CSP.connect);
  return out.trim() || null;
}
